import { FunctionComponent, useEffect, useRef, useState } from "react"
import { Table } from "@nova/ds-component-table"
import { ButtonPrimary, ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Status } from "@nova/ds-component-status"
import {
    useConfirmationModalContext,
    getGenericConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useS3FileUrl } from "plugins/shared/s3url"
import { useS3File } from "themes/default-tailwind/panels/GlobalUploadContainer"
import { downloadUri } from "tools/browser/download"
import { useAppSelector, useAppDispatch } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { AircraftFile } from "@nova/schemas"
import { isFilenameForImage, isFilenameForPdf } from "@nova/util-strings"
import { useAircraftFiles, useDeleteAircraftFile, aircraftFileS3Key } from "./AircraftFileQueries"
import { UploadAircraftFileModal } from "./UploadAircraftFileModal"
import { formatBytes } from "@nova/format"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ExpandableContent } from "@nova/component-expandable-content"
import classNames from "classnames"

const AircraftFilesTable = ({ aircraftId }: { aircraftId: string }) => {
    const { data: files, isLoading } = useAircraftFiles(aircraftId)
    const modal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()

    const handleUploadClick = async () => {
        await modal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <UploadAircraftFileModal
                    aircraftId={aircraftId}
                    onClose={cancel}
                    onSubmit={confirm}
                />
            ),
        })
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <h1 className="text-white text-[28px]">Files</h1>
                {isEditor && (
                    <ButtonTertiary
                        onClick={handleUploadClick}
                        title="Upload"
                        LeftIcon={Icons.Upload}
                    />
                )}
            </div>
            <Table>
                <Table.Head>
                    <Table.Header>
                        <div>Name</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Type</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Size</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Date uploaded</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Expires</div>
                    </Table.Header>
                </Table.Head>
                <Table.Body>
                    {isLoading && (
                        <Table.Row>
                            <Table.Cell className="text-gray-400">Loading...</Table.Cell>
                        </Table.Row>
                    )}
                    {!isLoading && !files?.length && (
                        <Table.Row>
                            <Table.Cell colSpan={5} className="h-[125px] text-center">
                                <div className="flex flex-col items-center justify-center gap-2 h-full">
                                    <h2 className="text-white">No Files Uploaded</h2>
                                    <p className="text-gray-400 text-sm text-center max-w-xs">
                                        Upload a file to add it to your records.
                                    </p>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {files?.map((file) => (
                        <AircraftFileRow key={file.id} file={file} aircraftId={aircraftId} />
                    ))}
                </Table.Body>
            </Table>
        </div>
    )
}

interface AircraftFileRowProps {
    file: AircraftFile
    aircraftId: string
}

const AircraftFileRow: FunctionComponent<AircraftFileRowProps> = ({ file, aircraftId }) => {
    const [isOpen, setIsOpen] = useState(false)
    const expandedRowRef = useRef<HTMLTableRowElement>(null)
    useEffect(() => {
        if (isOpen) expandedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }, [isOpen])
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const s3Key = orgId ? aircraftFileS3Key(orgId, aircraftId, file.id) : ""

    const { data: url } = useS3FileUrl(s3Key)
    const { deleteAircraftFile } = useDeleteAircraftFile(aircraftId)

    const isPdf = isFilenameForPdf(file.fileName)

    const { data: fileBlob } = useS3File(s3Key, { disabled: !isOpen || !isPdf })
    const [displayUrl, setDisplayUrl] = useState<string>()
    useEffect(() => {
        if (!fileBlob) return
        const blob = new Blob([fileBlob], { type: "application/pdf" })
        const objectUrl = URL.createObjectURL(blob)
        setDisplayUrl(objectUrl)
        return () => URL.revokeObjectURL(objectUrl)
    }, [fileBlob])

    const handleDownload = () => {
        if (!url) return
        downloadUri(file.fileName, url)
    }

    const modal = useConfirmationModalContext()
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()

    const handleDelete = () => {
        modal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Delete File?",
                message: `Are you sure you want to delete ${file.name}?`,
                confirmText: "Delete File",
            }),
            asyncConfirmCallback: async () => {
                try {
                    await deleteAircraftFile(file.id)
                    dispatch(
                        setGlobalNotificationContext({
                            message: `${file.name} has been deleted.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 5000,
                        })
                    )
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Failed to delete ${file.name}. Please try again.`,
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        })
    }

    const canPreview = isPdf || isFilenameForImage(file.fileName)
    const canExpand = canPreview || !!file.notes
    const isExpired = file.expiresAt && new Date(file.expiresAt).getTime() < Date.now()

    return (
        <>
            <Table.Row
                className="cursor-pointer relative group/fileRow"
                onClick={() => canExpand && setIsOpen((prev) => !prev)}
            >
                <Table.Cell className="flex items-center gap-2">
                    {canExpand ? (
                        <Icons.SmallIcon
                            Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                        />
                    ) : (
                        <div className="w-5" />
                    )}
                    {file.name}
                </Table.Cell>
                <Table.Cell>{file.fileName.split(".").pop()?.toUpperCase() ?? "--"}</Table.Cell>
                <Table.Cell>
                    {file.fileSizeBytes != null ? formatBytes(file.fileSizeBytes) : null}
                </Table.Cell>
                <Table.Cell>{new Date(file.createdAt).toDateString()}</Table.Cell>
                <Table.Cell>
                    {file.expiresAt && (
                        <Status type={isExpired ? "error" : "positive"} variant="outline">
                            {new Date(file.expiresAt).toDateString()}
                        </Status>
                    )}
                </Table.Cell>
                <Table.Cell
                    onClick={(e) => e.stopPropagation()}
                    className="absolute !p-0 h-full right-4 -top-0 flex items-center gap-4 opacity-0 group-hover/fileRow:opacity-100 bg-[linear-gradient(90deg,rgba(18,18,22,0)_0%,#121216_16.35%)]"
                >
                    <ButtonPrimary
                        title="Download"
                        LeftIcon={Icons.Download}
                        onClick={handleDownload}
                    />
                    {isEditor && (
                        <IconButton onClick={handleDelete}>
                            <Icons.SmallIcon Icon={Icons.Trash} />
                        </IconButton>
                    )}
                </Table.Cell>
            </Table.Row>
            <Table.Row ref={expandedRowRef}>
                <Table.Cell className={classNames("!p-0", { "!border-b-0": !isOpen })} colSpan={6}>
                    <ExpandableContent isOpen={isOpen}>
                        <div className="flex items-start gap-6 px-10 py-4">
                            {canPreview && (
                                <div className="w-[300px] h-[150px] shrink-0 overflow-hidden">
                                    {isPdf ? (
                                        displayUrl ? (
                                            <embed
                                                src={`${displayUrl}#toolbar=0&navpanes=0`}
                                                type="application/pdf"
                                                className="w-full h-full"
                                            />
                                        ) : (
                                            <div className="w-full h-full animate-pulse bg-gray-700" />
                                        )
                                    ) : url ? (
                                        <img
                                            src={url}
                                            alt={file.fileName ?? ""}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full animate-pulse bg-gray-700" />
                                    )}
                                </div>
                            )}
                            <h5 className="text-gray-100 max-w-[250px] whitespace-pre-wrap">
                                {file.notes}
                            </h5>
                        </div>
                    </ExpandableContent>
                </Table.Cell>
            </Table.Row>
        </>
    )
}

export { AircraftFilesTable }
